import { inject, Injector, provideAppInitializer, type EnvironmentProviders, type Provider } from '@angular/core';
import { tap } from 'rxjs';
import { MtmAuthClient, type Tenant } from '@mtm/auth';
import {
  AngularHttpAdapter,
  AngularNavigationAdapter,
  MtmAuthBridgeService,
  MtmNotificationService,
  MTM_AUTH_CLIENT,
  MTM_AUTH_CONFIG,
  MTM_CAPTCHA_SITE_KEY,
  MTM_LOGIN_ROUTE,
  MTM_NOTIFICATION_SERVICE,
  MTM_ON_APP_TOKEN_READY,
  MTM_ON_LOGIN_SUCCESS,
  MTM_ON_TENANT_REQUIRED,
  MTM_TRANSLATE_FN,
} from '@mtm/auth-ui-angular';
import authTranslations from '@mtm/auth-ui-angular/i18n/en';
import { environment } from '../../../environments/environment';
import { DriveAuthContextService } from '../drive/drive-auth-context.service';
import { MtmAuthGlueService } from '../services/mtm-auth-glue.service';
import {
  SwitchUserHandoffService,
  hasSwitchUserHandoff,
} from '../../admin/services/switch-user-handoff.service';
import { MtmIamStorageAdapter } from './mtm-iam-storage.adapter';
import { registerAppTokenSource } from './app-token';

const translations = authTranslations as Record<string, string>;

/**
 * Providers that wire the IAM auth SDK into this NgModule app. Spread into
 * `AppModule.providers`. The SDK callbacks delegate to {@link MtmAuthGlueService},
 * resolved lazily so the glue (and its bridge dependency) is only constructed
 * when a callback actually fires — well after bootstrap.
 */
export function provideMtmAuth(): (Provider | EnvironmentProviders)[] {
  return [
    // Admin "switch user" handed over by the platform admin console. Runs BEFORE the session
    // restore below: it writes the impersonation markers so that restore resolves as the
    // impersonated user instead of snapping back to the admin. Nothing is constructed on a
    // normal page load.
    provideAppInitializer(() => {
      const injector = inject(Injector);
      return hasSwitchUserHandoff() ? injector.get(SwitchUserHandoffService).consume$() : undefined;
    }),

    // Pass this app's auth/callback routes so the SDK recognizes them as auth routes and does NOT
    // persist them as the post-login returnUrl. Its defaults (`/`, `/login`, `/callback`,
    // `/tenant-select`, `/sso/*`) don't match this app's `/entry/...` paths, so without this the
    // SSO callback URL (with its one-time token) would be stored and re-entered after login.
    provideAppInitializer(() => {
      const bridge = inject(MtmAuthBridgeService);
      // Every legacy read of the app token (interceptor, header builders, players, downloads) goes through
      // `currentAppToken()`; point it at the SDK before anything issues a request.
      registerAppTokenSource(() => bridge.getAppTokenSync());
      // Eagerly construct the drive auth context and seed its tenantId once the
      // bridge is ready — the lazily-mounted drive surface otherwise misses the
      // appTokenUpdated$ emission from session restore, tenantId stays 0, and the
      // drive WebSocket never activates.
      const driveAuth = inject(DriveAuthContextService);
      return bridge
        .initialize$([
          '/entry/auth/process',
          '/entry/sso/callback',
          '/entry/auth/reset',
          '/entry/tenant-select',
          '/entry/iam/login',
          '/entry/login',
          '/entry/request-invitation',
        ])
        .pipe(tap(() => driveAuth.refresh()));
    }),

    {
      provide: MTM_AUTH_CLIENT,
      useFactory: (): MtmAuthClient =>
        new MtmAuthClient({
          baseUrl: environment.iam.iamApiUrl,
          appApiUrl: environment.iam.appApiUrl,
          appSsoCallback: environment.iam.ssoCallbackUrl,
          appResetPasswordCallback: environment.iam.resetPasswordCallbackUrl,
          driveApiUrl: environment.drive.apiUrl,
          serviceUuid: environment.iam.serviceUuid,
          ssoUrl: environment.iam.ssoUrl,
          tokenRenewal: { renewBeforeExpiryMs: 1_800_000, jitterMs: 60_000 },
          http: inject(AngularHttpAdapter),
          navigation: inject(AngularNavigationAdapter),
          // Give the SDK its own `auth_user` slot so it can never overwrite the legacy gateway user
          // the app relies on (the IAM identity lacks username/companyId/brandId).
          storage: new MtmIamStorageAdapter(),
        }),
    },

    { provide: MTM_TRANSLATE_FN, useValue: (key: string): string => translations[key] ?? key },
    { provide: MTM_CAPTCHA_SITE_KEY, useValue: environment.captchaSiteKey },
    { provide: MTM_LOGIN_ROUTE, useValue: '/entry/login' },
    { provide: MTM_NOTIFICATION_SERVICE, useExisting: MtmNotificationService },

    // Drives where the SDK registration/activation components navigate. `routes.verify`
    // is where `MtmAccountActivationComponent` self-navigates after a successful activation;
    // `routes.activate` is where the SSO callback sends an invited user back to (with
    // `?registration=…&sso=true`) to finish activating on the invitation page.
    {
      provide: MTM_AUTH_CONFIG,
      useValue: {
        termsAndConditionsUrl: '/mtm-tcp/view',
        routes: {
          verify: '/entry/on-boarding/email-verification',
          activate: '/entry/on-boarding/landing',
          resetPassword: environment.iam.resetPasswordCallbackUrl,
          requestInvitation: '/entry/request-invitation',
        },
      },
    },

    {
      provide: MTM_ON_LOGIN_SUCCESS,
      useFactory: (): (() => void) => {
        const injector = inject(Injector);
        return () => injector.get(MtmAuthGlueService).onLoginSuccess();
      },
    },
    {
      provide: MTM_ON_APP_TOKEN_READY,
      useFactory: (): ((token: string) => void) => {
        const injector = inject(Injector);
        return (token: string) => injector.get(MtmAuthGlueService).onAppTokenReady(token);
      },
    },
    {
      provide: MTM_ON_TENANT_REQUIRED,
      useFactory: (): ((tenants: Tenant[]) => void) => {
        const injector = inject(Injector);
        return (tenants: Tenant[]) => injector.get(MtmAuthGlueService).onTenantRequired(tenants);
      },
    },
  ];
}
